import * as React from 'react';
import {useEffect, useState} from "react";
import FileService from "../../services/FileService";
import FileBlobCache from "../../services/FileBlobCache";
import useDrive from "../../hooks/useDrive";
import {
  FolderRounded,
  HomeRounded,
  GroupsRounded,
  ViewInArRounded,
  MonitorHeartRounded,
  ImageRounded,
  PictureAsPdfRounded,
  FolderZipRounded,
  InsertDriveFileRounded,
  FolderSharedRounded,
} from "@mui/icons-material";

/* ─────────────────────────────────────────
   File-type → icon + colours mapping
───────────────────────────────────────── */
const EXT_MAP = {
  // 3D / CAD
  stl:  { Icon: ViewInArRounded,      bg: "rgba(124,106,247,0.18)", color: "#a78bfa", label: "3D" },
  obj:  { Icon: ViewInArRounded,      bg: "rgba(124,106,247,0.18)", color: "#a78bfa", label: "3D" },
  ply:  { Icon: ViewInArRounded,      bg: "rgba(124,106,247,0.18)", color: "#a78bfa", label: "3D" },
  // Medical imaging
  dcm:  { Icon: MonitorHeartRounded,  bg: "rgba(34,211,238,0.15)",  color: "#22d3ee", label: "DICOM" },
  dicom:{ Icon: MonitorHeartRounded,  bg: "rgba(34,211,238,0.15)",  color: "#22d3ee", label: "DICOM" },
  // Images
  png:  { Icon: ImageRounded,         bg: "rgba(251,191,36,0.15)",  color: "#fbbf24", label: "Image" },
  jpg:  { Icon: ImageRounded,         bg: "rgba(251,191,36,0.15)",  color: "#fbbf24", label: "Image" },
  jpeg: { Icon: ImageRounded,         bg: "rgba(251,191,36,0.15)",  color: "#fbbf24", label: "Image" },
  gif:  { Icon: ImageRounded,         bg: "rgba(251,191,36,0.15)",  color: "#fbbf24", label: "Image" },
  webp: { Icon: ImageRounded,         bg: "rgba(251,191,36,0.15)",  color: "#fbbf24", label: "Image" },
  // Documents / PDF
  pdf:  { Icon: PictureAsPdfRounded,  bg: "rgba(239,68,68,0.14)",   color: "#f87171", label: "PDF"   },
  // Archives
  zip:  { Icon: FolderZipRounded,     bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: "ZIP"   },
  "7z": { Icon: FolderZipRounded,     bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: "Archive" },
  tar:  { Icon: FolderZipRounded,     bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: "Archive" },
  gz:   { Icon: FolderZipRounded,     bg: "rgba(148,163,184,0.12)", color: "#94a3b8", label: "Archive" },
};

/* Folder variants */
const FOLDER_MAP = {
  home:    { Icon: HomeRounded,         bg: "rgba(232,88,120,0.18)",  color: "#e85878" },
  Patients:{ Icon: GroupsRounded,       bg: "rgba(251,191,36,0.18)",  color: "#fbbf24" },
  shared:  { Icon: FolderSharedRounded, bg: "rgba(52,211,153,0.15)",  color: "#34d399" },
  default: { Icon: FolderRounded,       bg: "rgba(124,106,247,0.15)", color: "#7c6af7" },
};

/* ── Resolve metadata for any row ── */
export function getRowMeta(row) {
  if (row.type === "folder") {
    const key = Object.keys(FOLDER_MAP).find(k => k !== "default" && row.Key === k) || "default";
    const isHome = row.Key?.trim() === "" || row.Key?.trim() === "/";
    return FOLDER_MAP[isHome ? "home" : key];
  }
  const ext = row.Key?.split(".").pop()?.toLowerCase();
  return EXT_MAP[ext] || { Icon: InsertDriveFileRounded, bg: "rgba(59,130,246,0.15)", color: "#3b82f6", label: ext?.toUpperCase() || "File" };
}

/* ── Icon tile — the large card thumbnail ── */
export function FileIconTile({ row, size = 52 }) {
  const { Icon, bg, color } = getRowMeta(row);
  return (
    <div style={{
      width: size, height: size,
      borderRadius: 12,
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
      <Icon sx={{ fontSize: size * 0.5, color }} />
    </div>
  );
}

/* ── Inline icon — used in the list-view row ── */
export function FileIconSmall({ row }) {
  const { Icon, bg, color } = getRowMeta(row);
  return (
    <div style={{
      width: 34, height: 34,
      borderRadius: 8,
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}>
      <Icon sx={{ fontSize: 18, color }} />
    </div>
  );
}

/* ── FileThumbnail: shows real image preview for images, icon for everything else ── */
export function FileThumbnail({ row, size = 52, fill = false }) {
  const [thumbUrl, setThumbUrl] = useState(null);
  const { CurrentDrive } = useDrive();

  useEffect(() => {
    let cancelled = false;
    setThumbUrl(null);
    if (row?.type === "file" && row.path && row.Key?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      FileService.getDownLoadLink("", row.path, CurrentDrive.driveType).then(async res => {
        if (!res || cancelled) return;
        const remoteUrl = res.url || (res.thumbnail ? `data:image/png;base64,${res.thumbnail}` : res);
        // Use cache so scrolling back doesn't re-download
        try {
          const blobUrl = await FileBlobCache.get(remoteUrl, row.path);
          if (!cancelled) setThumbUrl(blobUrl);
        } catch {
          if (!cancelled) setThumbUrl(remoteUrl);
        }
      });
    }
    return () => { cancelled = true; };
  }, [row?.path]);

  if (thumbUrl) {
    return (
      <img
        src={thumbUrl}
        alt={row.Key}
        style={fill
          ? { width: "100%", height: "100%", objectFit: "cover", display: "block" }
          : { width: size, height: size, objectFit: "cover", borderRadius: 10 }
        }
      />
    );
  }

  if (fill) {
    const { Icon, bg, color } = getRowMeta(row);
    return (
      <div style={{
        width: "100%", height: "100%",
        background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon sx={{ fontSize: "40%", color, minWidth: 60, minHeight: 60, maxWidth: 120, maxHeight: 120 }} />
      </div>
    );
  }

  return <FileIconTile row={row} size={size} />;
}
